import {
  Badge,
  Caption1,
  DataGrid,
  makeStyles,
  tokens,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  TableCell,
  TableRow,
  Tooltip,
  createTableColumn,
  mergeClasses,
  type TableColumnDefinition,
  type TableColumnId,
} from "@fluentui/react-components";
import { ChevronDown12Regular, ChevronRight12Regular, Warning12Regular } from "@fluentui/react-icons";
import { useMemo, useState, type KeyboardEvent } from "react";
import {
  DataTableFrame,
  isNumericCol,
  listColStyle,
  useDataTableStyles,
} from "@/components/data-table";
import { ConfirmDialog } from "@/components/app-dialog";
import { PersonAvatar } from "@/features/access/PersonAvatar";
import { useMockPlanAccess } from "@/features/access/mock-plan-access";
import {
  approveSameEmail,
  confirmMismatch,
  inviteResourceAccount,
  resourceLinkChip,
  unlinkResource,
  useResourceLinks,
  type ResourceAccount,
} from "@/features/access/resource-link";
import { suggestedPlanRoles } from "@/features/access/suggest-plan-role";
import type { OrganizationTeam, UserResource } from "@/engine/types";
import { fmtCurrency } from "@/lib/format";
import { useCatalogStore, type ResourceWorkloadSummary } from "@/store/catalog-store";
import { PersonRowTrigger, RowMoreMenu } from "./RowMoreMenu";
import { EMPLOYMENT_LABEL, GRADE_LABEL } from "./resource-meta";
import { TeamMark } from "./team-appearance";

export type ResourceListGroup = "none" | "team" | "employment" | "project" | "grade";

type SortState = {
  sortColumn: TableColumnId | undefined;
  sortDirection: "ascending" | "descending";
};

type PersonRow = {
  rowId: string;
  groupKey: string;
  groupLabel: string;
  groupColor?: string;
  groupIcon?: string;
  groupHint?: string;
  resource: UserResource;
  name: string;
  role: string;
  teamName: string;
  teamMissing: boolean;
  employment: string;
  grade: string;
  pct: number;
  over: boolean;
  plans: ResourceWorkloadSummary["assignments"];
  rate: number;
};

const SORTABLE = new Set<TableColumnId>(["name", "team", "employment", "grade", "alloc", "rate"]);

/** 플랜 목록 표와 같은 제목 열. DataGrid는 flex라 남는 폭은 `listColStyle`의 이름 열이 흡수한다. */
const useListStyles = makeStyles({
  nameCol: {
    minWidth: "220px",
    width: "100%",
    position: "sticky",
    left: 0,
    zIndex: 1,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    overflow: "hidden",
    boxSizing: "border-box",
  },
  nameHead: {
    zIndex: 3,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  /** 제목 오른쪽 열. 기본 셀(8px)보다 좌우 여백을 줄여 붙인다. */
  metric: {
    paddingLeft: tokens.spacingHorizontalSNudge,
    paddingRight: tokens.spacingHorizontalSNudge,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
  },
  hoverRow: {
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    /** Fluent가 셀 사이에 `<i>`를 끼워 첫 자식은 이름 칸이 아니다. */
    ":hover > [role='gridcell']:first-of-type": { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
});

function compareRows(a: PersonRow, b: PersonRow, sort: SortState): number {
  const dir = sort.sortDirection === "descending" ? -1 : 1;
  const col = sort.sortColumn ?? "name";
  let n = 0;
  switch (col) {
    case "team":
      n = a.teamName.localeCompare(b.teamName, "ko");
      break;
    case "employment":
      n = a.employment.localeCompare(b.employment, "ko");
      break;
    case "grade":
      n = a.grade.localeCompare(b.grade, "ko");
      break;
    case "alloc":
      n = a.pct - b.pct;
      break;
    case "rate":
      n = a.rate - b.rate;
      break;
    default:
      n = a.name.localeCompare(b.name, "ko");
  }
  if (n === 0) n = a.name.localeCompare(b.name, "ko");
  return n * dir;
}

function PlansCell({
  plans,
  onOpenProject,
}: {
  plans: ResourceWorkloadSummary["assignments"];
  onOpenProject: (projectId: string) => void;
}) {
  const styles = useDataTableStyles();
  if (plans.length === 0) return <span className={styles.muted}>—</span>;
  if (plans.length === 1) {
    const plan = plans[0]!;
    const label = `${plan.projectName} (${Math.round(plan.allocationRatio * 100)}%)`;
    return (
      <Tooltip content={label} relationship="description">
        <button
          type="button"
          className={styles.linkBtn}
          onClick={(e) => {
            e.stopPropagation();
            onOpenProject(plan.projectId);
          }}
        >
          {plan.projectName}
        </button>
      </Tooltip>
    );
  }
  return (
    <Popover>
      <PopoverTrigger disableButtonEnhancement>
        <button type="button" className={styles.linkBtn} onClick={(e) => e.stopPropagation()}>
          {plans.length}개 플랜
        </button>
      </PopoverTrigger>
      <PopoverSurface>
        <div className={styles.planList} role="list">
          {plans.map((plan) => (
            <button
              key={`${plan.projectId}-${plan.projectRole}`}
              type="button"
              role="listitem"
              className={styles.planItem}
              onClick={(e) => {
                e.stopPropagation();
                onOpenProject(plan.projectId);
              }}
            >
              <span className={styles.clip}>{plan.projectName}</span>
              <span className={styles.num}>{Math.round(plan.allocationRatio * 100)}%</span>
            </button>
          ))}
        </div>
      </PopoverSurface>
    </Popover>
  );
}

export function ResourceListTable({
  resources,
  teams,
  teamOrder,
  workloads,
  groupBy,
  masked,
  onOpen,
  onEdit,
  onDelete,
  onOpenProject,
  className,
}: {
  resources: UserResource[];
  teams: Record<string, OrganizationTeam>;
  teamOrder: OrganizationTeam[];
  workloads: Record<string, ResourceWorkloadSummary>;
  groupBy: ResourceListGroup;
  masked: boolean;
  onOpen: (resource: UserResource) => void;
  onEdit: (resource: UserResource) => void;
  onDelete: (resource: UserResource) => void;
  onOpenProject: (projectId: string) => void;
  className?: string;
}) {
  const styles = useDataTableStyles();
  const list = useListStyles();
  const [pendingLink, setPendingLink] = useState<{ resource: UserResource; account: ResourceAccount } | null>(null);
  const [sort, setSort] = useState<SortState>({ sortColumn: "name", sortDirection: "ascending" });
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const [collapseFor, setCollapseFor] = useState(groupBy);
  if (collapseFor !== groupBy) {
    setCollapseFor(groupBy);
    setCollapsed(new Set());
  }

  const columns = useMemo<TableColumnDefinition<PersonRow>[]>(
    () => [
      createTableColumn<PersonRow>({
        columnId: "name",
        compare: (a, b) => a.rowId.localeCompare(b.rowId) - a.rowId.localeCompare(b.rowId),
        renderHeaderCell: () => "인력 / 역할",
        renderCell: (item) => (
          <ResourceNameCell item={item} onOpen={onOpen} onEdit={onEdit} onDelete={onDelete} onConfirm={setPendingLink} />
        ),
      }),
      createTableColumn<PersonRow>({
        columnId: "team",
        compare: (a, b) => a.rowId.localeCompare(b.rowId) - a.rowId.localeCompare(b.rowId),
        renderHeaderCell: () => "팀",
        renderCell: (item) => (
          <Tooltip content={item.teamName} relationship="description">
            <span className={mergeClasses(styles.clip, item.teamMissing && styles.muted)}>{item.teamName}</span>
          </Tooltip>
        ),
      }),
      createTableColumn<PersonRow>({
        columnId: "employment",
        compare: (a, b) => a.rowId.localeCompare(b.rowId) - a.rowId.localeCompare(b.rowId),
        renderHeaderCell: () => "형태",
        renderCell: (item) => <span className={styles.clip}>{item.employment}</span>,
      }),
      createTableColumn<PersonRow>({
        columnId: "grade",
        compare: (a, b) => a.rowId.localeCompare(b.rowId) - a.rowId.localeCompare(b.rowId),
        renderHeaderCell: () => "등급",
        renderCell: (item) => <span className={styles.clip}>{item.grade}</span>,
      }),
      createTableColumn<PersonRow>({
        columnId: "alloc",
        compare: (a, b) => a.rowId.localeCompare(b.rowId) - a.rowId.localeCompare(b.rowId),
        renderHeaderCell: () => <span className={styles.numLabel}>가동률</span>,
        renderCell: (item) => (
          <span className={mergeClasses(styles.allocValue, item.over && styles.danger)}>
            {item.pct}%
            {item.over && <Warning12Regular />}
          </span>
        ),
      }),
      createTableColumn<PersonRow>({
        columnId: "plans",
        compare: () => 0,
        renderHeaderCell: () => "투입",
        renderCell: (item) => <PlansCell plans={item.plans} onOpenProject={onOpenProject} />,
      }),
      createTableColumn<PersonRow>({
        columnId: "rate",
        compare: (a, b) => a.rowId.localeCompare(b.rowId) - a.rowId.localeCompare(b.rowId),
        renderHeaderCell: () => <span className={styles.numLabel}>월 단가</span>,
        renderCell: (item) => fmtCurrency(item.resource.defaultMonthlyRate, masked),
      }),
    ],
    [masked, onDelete, onEdit, onOpen, onOpenProject, styles],
  );

  const people = useMemo(() => {
    const rows = expandRows(resources, teams, workloads, groupBy);
    return orderRows(rows, sort, groupBy, teamOrder);
  }, [resources, teams, workloads, groupBy, sort, teamOrder]);

  const groupCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of people) {
      if (!row.groupKey) continue;
      counts.set(row.groupKey, (counts.get(row.groupKey) ?? 0) + 1);
    }
    return counts;
  }, [people]);

  const seen = { current: null as string | null };

  return (
    <>
    <DataTableFrame className={className}>
      <DataGrid
        items={people}
        columns={columns}
        getRowId={(item) => item.rowId}
        sortable
        sortState={sort}
        onSortChange={(_, next) => {
          if (!next.sortColumn || !SORTABLE.has(next.sortColumn)) return;
          setSort({ sortColumn: next.sortColumn, sortDirection: next.sortDirection });
        }}
        focusMode="composite"
        size="small"
        className={styles.grid}
        aria-label="인력 목록"
      >
        <DataGridHeader className={styles.header}>
          <DataGridRow className={styles.headerRow}>
            {(column) => (
              <DataGridHeaderCell
                className={mergeClasses(
                  styles.headerCell,
                  column.columnId === "name" ? list.nameCol : list.metric,
                  column.columnId === "name" && list.nameHead,
                  isNumericCol(column.columnId) && styles.num,
                )}
                style={listColStyle(column.columnId)}
              >
                {column.renderHeaderCell()}
              </DataGridHeaderCell>
            )}
          </DataGridRow>
        </DataGridHeader>
        <DataGridBody<PersonRow>>
          {({ item }) => {
            const showGroup = Boolean(item.groupKey) && item.groupKey !== seen.current;
            seen.current = item.groupKey;
            const isCollapsed = collapsed.has(item.groupKey);
            return (
              <>
                {showGroup && (
                  <GroupHeader
                    label={item.groupLabel}
                    color={item.groupColor}
                    icon={item.groupIcon}
                    hint={item.groupHint}
                    count={groupCounts.get(item.groupKey) ?? 0}
                    collapsed={isCollapsed}
                    onToggle={() =>
                      setCollapsed((prev) => {
                        const next = new Set(prev);
                        if (next.has(item.groupKey)) next.delete(item.groupKey);
                        else next.add(item.groupKey);
                        return next;
                      })
                    }
                  />
                )}
                {isCollapsed ? null : (
                  <DataGridRow<PersonRow>
                    className={mergeClasses(styles.row, styles.identityRow, list.hoverRow, item.resource.isActive ? undefined : styles.inactive)}
                    onClick={() => onOpen(item.resource)}
                    onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
                      if (e.key === "Enter" && e.target === e.currentTarget) onOpen(item.resource);
                    }}
                  >
                    {(column) => (
                      <DataGridCell
                        focusMode={column.columnId === "name" || column.columnId === "plans" ? "group" : "cell"}
                        className={mergeClasses(
                          styles.cell,
                          column.columnId === "name" ? list.nameCol : list.metric,
                          isNumericCol(column.columnId) && styles.num,
                          column.columnId === "alloc" && item.over && styles.danger,
                        )}
                        style={listColStyle(column.columnId)}
                      >
                        {column.renderCell(item)}
                      </DataGridCell>
                    )}
                  </DataGridRow>
                )}
              </>
            );
          }}
        </DataGridBody>
      </DataGrid>
    </DataTableFrame>
    <ConfirmDialog
      open={pendingLink !== null}
      title="계정 연결"
      message="이메일이 다릅니다. 연결해도 배정은 그대로입니다."
      confirmLabel="연결"
      cancelLabel="취소"
      onCancel={() => setPendingLink(null)}
      onConfirm={() => {
        if (pendingLink) confirmMismatch(pendingLink.resource, pendingLink.account);
        setPendingLink(null);
      }}
    />
    </>
  );
}

function ResourceNameCell({
  item,
  onOpen,
  onEdit,
  onDelete,
  onConfirm,
}: {
  item: PersonRow;
  onOpen: (resource: UserResource) => void;
  onEdit: (resource: UserResource) => void;
  onDelete: (resource: UserResource) => void;
  onConfirm: (pending: { resource: UserResource; account: ResourceAccount }) => void;
}) {
  const styles = useDataTableStyles();
  useResourceLinks();
  const members = useMockPlanAccess().members;
  const accounts = members
    .filter((member) => member.status === "활성")
    .map((member) => ({ id: member.id, name: member.name, email: member.email }));
  const chip = resourceLinkChip(item.resource, accounts);
  const projects = useCatalogStore((s) => s.projects);
  const suggestions = suggestedPlanRoles(
    chip === "연결됨",
    Object.values(projects).flatMap((project) =>
      (project.resourceAssignments ?? [])
        .filter((assignment) => assignment.resourceId === item.resource.id)
        .map((assignment) => assignment.accessRole),
    ),
  );
  const mismatch = accounts.find((account) => account.name.trim() === item.name.trim() && account.email.trim().toLowerCase() !== item.resource.email.trim().toLowerCase());
  const actions = [
    { label: "프로필 보기", onClick: () => onOpen(item.resource) },
    { label: "수정", onClick: () => onEdit(item.resource) },
    { label: "삭제", onClick: () => onDelete(item.resource) },
  ];
  if (chip === "연결 제안") actions.push({ label: "연결 승인", onClick: () => { approveSameEmail(item.resource, accounts); } });
  if (chip === "확인 필요" && mismatch) actions.push({ label: "연결 확인", onClick: () => onConfirm({ resource: item.resource, account: mismatch }) });
  if (chip === "연결됨") actions.push({ label: "연결 해제", onClick: () => unlinkResource(item.resource.id) });
  if (chip === "계정 없음" && item.resource.email.trim()) actions.push({ label: "계정 초대", onClick: () => inviteResourceAccount(item.resource.id) });
  return (
    <RowMoreMenu label={`${item.name} 메뉴`} items={actions}>
      <PersonRowTrigger label={`${item.name} 메뉴`} onClick={(e) => e.stopPropagation()}>
        <span className={styles.person}>
          <PersonAvatar name={item.name} size={28} tip={false} />
          <span className={styles.personText}>
            <span className={styles.nameLine}>
              <Tooltip content={item.name} relationship="description">
                <span className={mergeClasses(styles.clip, styles.personName)}>{item.name}</span>
              </Tooltip>
              {suggestions.map((role) => (
                <Tooltip key={role} content="제안만입니다. 권한은 바뀌지 않습니다." relationship="description">
                  <span>
                    <Badge appearance="outline" color="subtle" size="small">{`제안 ${role}`}</Badge>
                  </span>
                </Tooltip>
              ))}
            </span>
            {item.role ? (
              <Tooltip content={item.role} relationship="description">
                <Caption1 className={styles.role}>{item.role}</Caption1>
              </Tooltip>
            ) : null}
          </span>
        </span>
      </PersonRowTrigger>
    </RowMoreMenu>
  );
}

function GroupHeader({
  label,
  color,
  icon,
  hint,
  count,
  collapsed,
  onToggle,
}: {
  label: string;
  color?: string;
  icon?: string;
  hint?: string;
  count: number;
  collapsed: boolean;
  onToggle: () => void;
}) {
  const styles = useDataTableStyles();
  return (
    <TableRow
      className={styles.groupRow}
      aria-expanded={!collapsed}
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle();
        }
      }}
    >
      <TableCell className={styles.groupCell} style={{ flex: "1 1 auto", width: "100%" }}>
        <span className={styles.groupInner}>
          {collapsed ? <ChevronRight12Regular /> : <ChevronDown12Regular />}
          {(icon || color) && <TeamMark icon={icon} color={color} />}
          <span className={styles.groupLabel}>{label}</span>
          <Badge appearance="tint" color="subtle" size="small">
            {count}명
          </Badge>
          {hint && <Caption1 className={mergeClasses(styles.clip, styles.muted)}>{hint}</Caption1>}
        </span>
      </TableCell>
    </TableRow>
  );
}

function expandRows(
  resources: UserResource[],
  teams: Record<string, OrganizationTeam>,
  workloads: Record<string, ResourceWorkloadSummary>,
  groupBy: ResourceListGroup,
): PersonRow[] {
  const baseOf = (resource: UserResource) => {
    const workload = workloads[resource.id];
    const team = resource.teamId ? teams[resource.teamId] : undefined;
    return {
      resource,
      name: resource.displayName,
      role: resource.roleTitle,
      teamName: team?.name ?? "소속 없음",
      teamMissing: !team,
      employment: EMPLOYMENT_LABEL[resource.employmentType],
      grade: GRADE_LABEL[resource.grade],
      pct: Math.round((workload?.totalAllocationRatio ?? 0) * 100),
      over: Boolean(workload?.isOverAllocated),
      plans: workload?.assignments ?? [],
      rate: resource.defaultMonthlyRate ?? 0,
    };
  };

  if (groupBy !== "project") {
    return resources.map((resource) => {
      const team = resource.teamId ? teams[resource.teamId] : undefined;
      let groupKey = "";
      let groupLabel = "";
      let groupColor: string | undefined;
      let groupIcon: string | undefined;
      let groupHint: string | undefined;
      if (groupBy === "team") {
        groupKey = team?.id ?? "__none";
        groupLabel = team?.name ?? "소속 없음";
        groupColor = team?.color;
        groupIcon = team?.icon;
        groupHint = team?.description;
      } else if (groupBy === "employment") {
        groupKey = resource.employmentType;
        groupLabel = EMPLOYMENT_LABEL[resource.employmentType];
      } else if (groupBy === "grade") {
        groupKey = resource.grade;
        groupLabel = GRADE_LABEL[resource.grade];
      }
      return {
        ...baseOf(resource),
        rowId: groupKey ? `${groupKey}:${resource.id}` : resource.id,
        groupKey,
        groupLabel,
        groupColor,
        groupIcon,
        groupHint,
      };
    });
  }

  const rows: PersonRow[] = [];
  for (const resource of resources) {
    const base = baseOf(resource);
    const workload = workloads[resource.id];
    const assignments = workload?.assignments ?? [];
    if (assignments.length === 0) {
      rows.push({
        ...base,
        rowId: `__idle:${resource.id}`,
        groupKey: "__idle",
        groupLabel: "투입 대기",
      });
    }
    for (const assignment of assignments) {
      rows.push({
        ...base,
        rowId: `${assignment.projectId}:${assignment.projectRole}:${resource.id}`,
        groupKey: assignment.projectId,
        groupLabel: assignment.projectName,
      });
    }
  }
  return rows;
}

function orderRows(rows: PersonRow[], sort: SortState, groupBy: ResourceListGroup, teamOrder: OrganizationTeam[]): PersonRow[] {
  const sorted = [...rows].sort((a, b) => compareRows(a, b, sort));
  if (groupBy === "none") return sorted;

  const buckets = new Map<string, PersonRow[]>();
  for (const row of sorted) {
    const list = buckets.get(row.groupKey) ?? [];
    list.push(row);
    buckets.set(row.groupKey, list);
  }

  const keys =
    groupBy === "team"
      ? [...teamOrder.map((team) => team.id), "__none"].filter((key) => (buckets.get(key)?.length ?? 0) > 0)
      : [...buckets.keys()].sort((a, b) => (buckets.get(b)?.length ?? 0) - (buckets.get(a)?.length ?? 0) || a.localeCompare(b, "ko"));

  return keys.flatMap((key) => buckets.get(key) ?? []);
}
